import { containedButtonStyle } from '@/theme';
import { Box, Button, Popover, Typography } from '@mui/material';
import { ArrowDropDown } from '@mui/icons-material';
import { useState } from 'react';
import { abyssColors } from '@/theme/colors';
import { RiskFieldsTypeEnum } from '@/__generated__/graphql';
import { useRouter } from '@/hooks';
import { riskView, selectedRiskTab } from '../state/atoms';
import { useAtomValue, useSetAtom } from 'jotai';
import { selectedRiskAssessmentFilters } from '../../state';

const { Pof, Cof, TransitionPeriod } = RiskFieldsTypeEnum;

const calculationsMenu = [
  {
    key: Pof,
    label: 'POF',
    description:
      'Calculate the likelihood that a specific piece of equipment or asset will fail over a defined period',
  },
  {
    key: Cof,
    label: 'COF',
    description: ' Assign the impact of a failure if it occurs, focusing on SHE & Financials',
  },
  {
    key: TransitionPeriod,
    label: 'Transition Period',
    description:
      'Calculate the time frame in which an asset transitions from a functional state to a state where the risk of failure significantly increases',
  },
];

export const CalculationsMenu = () => {
  const [calculationsPopover, setCalculationsPopover] = useState<HTMLButtonElement | undefined>(
    undefined
  );
  const setSelectedTab = useSetAtom(selectedRiskTab);

  const selectedFilters = useAtomValue(selectedRiskAssessmentFilters);

  const setView = useSetAtom(riskView);

  const isCalculationsPopoverOpen = Boolean(calculationsPopover);
  const router = useRouter();

  const handleCalculations = (calculationType: RiskFieldsTypeEnum) => {
    setSelectedTab(calculationType);
    setCalculationsPopover(undefined);
    const currentQuery = { ...router.query, view: 'calculations' };
    router.push({
      pathname: router.pathname,
      query: currentQuery,
    });
    setView('calculations');
  };

  return (
    <>
      <Button
        variant="contained"
        sx={{
          ...containedButtonStyle,
          '&.Mui-disabled': {
            color: containedButtonStyle.color || 'inherit',
            opacity: 1,
          },
        }}
        endIcon={
          <ArrowDropDown
            sx={{
              transform: `rotate(${isCalculationsPopoverOpen ? '180deg' : '0deg'})`,
              color: containedButtonStyle.color || 'inherit',
            }}
          />
        }
        onClick={(event) => setCalculationsPopover(event.currentTarget)}
        disabled={selectedFilters.length === 0}
      >
        Calculations
      </Button>
      <Popover
        elevation={2}
        open={isCalculationsPopoverOpen}
        anchorEl={calculationsPopover}
        onClose={() => setCalculationsPopover(undefined)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        transformOrigin={{ vertical: 'top', horizontal: 'right' }}
        sx={{ mt: 1 }}
        slotProps={{ paper: { sx: { width: 350 } } }}
      >
        {calculationsMenu.map(({ key, label, description }) => (
          <Box
            key={key}
            sx={{
              px: 2,
              py: 1.5,
              cursor: 'pointer',
              '&:hover': {
                backgroundColor: abyssColors.primary[100],
              },
            }}
            onClick={() => handleCalculations(key)}
          >
            <Typography mb={0.5} fontWeight={600} variant="body1">
              {label}
            </Typography>
            <Typography variant="caption">{description}</Typography>
          </Box>
        ))}
      </Popover>
    </>
  );
};
